// src/features/messages/ConversationRow.tsx — one conversation-list row.
import styled from 'styled-components';
import { Avatar } from '@/ui';
import { mediaLabel } from './meta';
import { useOnlineOverride } from '@/core/stores/presenceStore';
import type { Conversation } from '@/core/models/message';

const Row = styled.button<{ $active: boolean }>`
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
  padding: 10px 12px; border: none; cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $active }) => ($active ? theme.color.glassBorder : 'transparent')};
  &:hover { background: ${({ theme }) => theme.color.hover}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.accentCyan}; outline-offset: -2px; }
`;
const AvatarWrap = styled.div`position: relative; flex: none;`;
const Dot = styled.span`
  position: absolute; right: 0; bottom: 0; width: 10px; height: 10px; border-radius: 50%;
  background: ${({ theme }) => theme.color.success};
  border: 2px solid ${({ theme }) => theme.color.bgRoot};
`;
const Info = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px;`;
const TopLine = styled.div`display: flex; align-items: baseline; gap: 8px;`;
const Name = styled.span`
  flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600; color: ${({ theme }) => theme.color.textPrimary};
`;
const Time = styled.span`
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted}; flex: none;
`;
const Preview = styled.span`
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const Members = styled.span`margin-right: 0.9em;`;
const Unread = styled.span`
  flex: none; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px;
  display: inline-grid; place-items: center;
  font-family: ${({ theme }) => theme.font.mono}; font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 700; color: ${({ theme }) => theme.color.onAccent};
  background: ${({ theme }) => theme.color.accentCyan};
`;

export interface ConversationRowProps {
  convo: Conversation;
  active: boolean;
  onSelect: (id: string) => void;
}

export function ConversationRow({ convo, active, onSelect }: ConversationRowProps) {
  const last = convo.messages[convo.messages.length - 1];
  // 1.24.00: a real image attachment with no caption previews as "Photo" — the
  // row must not look like an empty message was sent. Text always wins when
  // there is any; the legacy decorative `media` flag keeps its own branch.
  const preview = last
    ? last.media
      ? mediaLabel(last.media)
      : last.body !== ''
        ? last.body
        : last.image
          ? mediaLabel('image')
          : ''
    : '';
  // 1.21.00: a live `presence` event overrides the REST-hydrated `online`
  // (which only reflects presence as of the last fetch/poll); falls back to
  // that REST value whenever no presence event has arrived yet for this user.
  //
  // 1.24.00: `online === null` means presence is HIDDEN (either side turned
  // Activity status off) and outranks any live event — a stale `presence`
  // frame from before the switch flipped must not resurrect the dot.
  const liveOnline = useOnlineOverride(convo.otherUserId ?? undefined);
  const online = convo.online === null ? null : (liveOnline ?? convo.online);

  return (
    <Row type="button" $active={active} aria-current={active} onClick={() => onSelect(convo.id)}>
      <AvatarWrap>
        <Avatar name={convo.name} size={42} />
        {convo.type === 'dm' && online === true && (
          <Dot aria-hidden="true" data-testid="online-dot" />
        )}
      </AvatarWrap>
      <Info>
        <TopLine>
          <Name>{convo.name}</Name>
          <Time>{convo.time}</Time>
        </TopLine>
        <Preview>
          {convo.type === 'group' && <Members>{convo.members ?? 0} members</Members>}
          {preview}
        </Preview>
      </Info>
      {convo.unread > 0 && <Unread aria-label={`${convo.unread} unread`}>{convo.unread}</Unread>}
    </Row>
  );
}
